import React, { FC } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import camelCase from "camelcase";
import Head from "next/head";
import { withCdnUrl } from "@smile2impress/attraction-utils";
import { STRAPI_WEBSITE_API_URL } from "api/cms-website";

import { getImageUrl } from "utils/imageUrl";

import { MetaTagsProps } from "./MetaTags.types";

export const MetaTags: FC<MetaTagsProps> = ({ title, description, seo }) => {
    const { t } = useTranslation("meta");
    const router = useRouter();
    const pageName = router?.pathname?.slice(1);
    const camelName = pageName ? camelCase(pageName) : "main";

    const metaTitle =
        title ?? seo?.metaTitle ?? t(`${camelName}Title`, { fallback: "" });
    const metaDescription =
        description ??
        seo?.metaDescription ??
        t<string>(`${camelName}Description`, { fallback: "" });
    const metaImage = seo?.metaImage?.url;

    const ogTitle = seo?.openGraph?.ogTitle ?? metaTitle;
    const ogDescription = seo?.openGraph?.ogDescription ?? metaDescription;
    const ogImage = seo?.openGraph?.ogImage?.url ?? metaImage;
    const socialImageAbsolute = getImageUrl(ogImage, STRAPI_WEBSITE_API_URL);
    const ogImageFinal =
        socialImageAbsolute || withCdnUrl("/images/fav/og-image.jpg?v=1");
    const keywords = seo?.keywords ?? "Impress";

    return (
        <Head>
            <title key="metaTitle">{metaTitle}</title>
            <meta
                name="description"
                key="metaDescription"
                content={metaDescription}
            />
            <meta name="keywords" key="keywords" content={keywords} />
            {seo?.metaRobots && (
                <meta name="robots" key="robots" content={seo.metaRobots} />
            )}
            {/* canonical and hreflang — rendered by CanonicalService and HreflangService (_app) with override from pageProps */}
            {/* Open Graph */}
            <meta property="og:title" content={ogTitle} />
            <meta property="og:description" content={ogDescription} />
            <meta
                property="og:type"
                content={seo?.openGraph?.ogType ?? "website"}
            />
            {seo?.openGraph?.ogUrl && (
                <meta property="og:url" content={seo.openGraph.ogUrl} />
            )}
            <meta property="og:image" content={ogImageFinal} />
            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={ogTitle} />
            <meta name="twitter:description" content={ogDescription} />
            <meta name="twitter:image" content={ogImageFinal} />
        </Head>
    );
};
